import { QuestionnaireFlowQuestion } from "@/types/question.types";
import { usePostHog } from "posthog-js/react";

export function usePostHogQuestionnaireTracking() {
  const posthog = usePostHog();

  const trackFlowStarted = (flowType: string) => {
    // Prevent duplicate flow_started events in the same session
    const sessionKey = `posthog_flow_started_${flowType}`;
    if (sessionStorage.getItem(sessionKey)) {
      return; // Already sent this flow_started event
    }

    posthog.capture("questionnaire_flow_started", {
      flow_type: flowType,
    });

    sessionStorage.setItem(sessionKey, "true");
  };

  const trackQuestionViewed = (
    flowType: string,
    question: QuestionnaireFlowQuestion,
    stepNumber: number,
    totalSteps: number
  ) => {
    // Prevent duplicate question_viewed events for the same question in rapid succession
    const sessionKey = `posthog_last_viewed_${flowType}_${question.slug}`;
    const lastViewed = sessionStorage.getItem(sessionKey);
    const now = Date.now();

    // Only fire if we haven't viewed this question in the last 2 seconds (prevents remount duplicates)
    if (lastViewed && now - parseInt(lastViewed) < 2000) {
      return;
    }

    posthog.capture("questionnaire_question_viewed", {
      flow_type: flowType,
      question_slug: question.slug,
      question_title: question.title,
      question_type: question.type,
      question_category: question.category,
      step_number: stepNumber + 1,
      total_steps: totalSteps,
      progress_percentage: Math.round(((stepNumber + 1) / totalSteps) * 100),
    });

    sessionStorage.setItem(sessionKey, now.toString());
  };

  const trackQuestionAnswered = (
    flowType: string,
    question: QuestionnaireFlowQuestion,
    stepNumber: number
  ) => {
    posthog.capture("questionnaire_question_answered", {
      flow_type: flowType,
      question_slug: question.slug,
      question_title: question.title,
      step_number: stepNumber + 1,
    });
  };

  const trackFlowCompleted = async (flowType: string, totalQuestions: number): Promise<void> => {
    posthog.capture("questionnaire_flow_completed", {
      flow_type: flowType,
      total_questions: totalQuestions,
    });

    // Clear the flow_started flag so user can restart this flow later
    sessionStorage.removeItem(`posthog_flow_started_${flowType}`);

    // Wait 200ms for PostHog to send the event before navigating
    return new Promise((resolve) => setTimeout(resolve, 200));
  };

  const trackFlowAbandoned = (
    flowType: string,
    lastQuestionSlug: string,
    stepNumber: number,
    totalSteps: number
  ) => {
    posthog.capture("questionnaire_flow_abandoned", {
      flow_type: flowType,
      last_question_slug: lastQuestionSlug,
      step_reached: stepNumber + 1,
      total_steps: totalSteps,
      abandonment_percentage: Math.round(((stepNumber + 1) / totalSteps) * 100),
    });
  };

  return {
    trackFlowStarted,
    trackQuestionViewed,
    trackQuestionAnswered,
    trackFlowCompleted,
    trackFlowAbandoned,
  };
}
